import { useMediaQuery } from "react-responsive";

import { StoreLocatorCardV2 } from "@locators/components/shared/components/StoreLocatorCardV2";
import { getStoreDetailsPageUrl } from "@locators/components/shared/utils";
import type { StoreExtendedParams } from "@locators/components/StoreLocator";
import { StoreLocatorCard, useStoreTrackingEvent } from "@locators/components/StoreLocator";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore } from "@jti/fe-common";
import { Carousel } from "@jti/ui/components/Carousel";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import styles from "./StoreDetailsNearbyShops.module.scss";

type StoreDetailsNearbyShopsProps = {
  stores: FlotiqStore[];
  storeDetailsPage?: string;
  defaultZoom?: number;
} & StoreExtendedParams;

type StoreDetailsNearbyShopsCardProps = {
  store: FlotiqStore;
  storeDetailsPage?: string;
  defaultZoom?: number;
} & StoreExtendedParams;

const BREAKPOINT = 1024;

const StoreDetailsNearbyShopsCard = ({
  store,
  storeDetailsPage,
  defaultZoom,
  isOpeningHoursEnabled,
  isOpeningHoursBreaksEnabled,
  isAvailableDevicesEnabled,
  isAvailableSticksEnabled,
  isMultiBrandSticksEnabled,
  isGeneralAnnouncementEnabled,
  isAvailableServicesEnabled,
  isStoreTypeEnabled,
  isVendingMachineEnabled,
  isVendingMachineTipEnabled,
  isNearbyShopsEnabled,
}: StoreDetailsNearbyShopsCardProps) => {
  const config = useConfig();
  const storeDetailsPageUrl = getStoreDetailsPageUrl({
    storeDetailsPage: storeDetailsPage ?? "",
    store,
    defaultZoom,
    isOpeningHoursEnabled,
    isOpeningHoursBreaksEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    isMultiBrandSticksEnabled,
    isGeneralAnnouncementEnabled,
    isAvailableServicesEnabled,
    isStoreTypeEnabled,
    isVendingMachineEnabled,
    isVendingMachineTipEnabled,
    isNearbyShopsEnabled,
    isSeoFriendlyUrlsEnabled: config.isSeoFriendlyStoreLocatorEnabled,
  });

  const publishStoreTrackingEvent = useStoreTrackingEvent();

  const isMobileOrTablet = useMediaQuery({ maxWidth: BREAKPOINT });

  if (isMobileOrTablet) {
    return <StoreLocatorCardV2 isListView store={store} storeDetailsPageUrl={storeDetailsPageUrl} />;
  }

  return (
    <StoreLocatorCard
      isActive={false}
      store={store}
      isListView={true}
      storeDetailsPageUrl={storeDetailsPageUrl}
      className={styles.storeLocatorCard}
      onGetDirectionClick={() => {
        publishStoreTrackingEvent(store.id, store.StoreName, store.storetype ?? "");
      }}
    />
  );
};

export const StoreDetailsNearbyShops = ({ stores, ...rest }: StoreDetailsNearbyShopsProps) => {
  const { __ } = useTranslations();

  return (
    <div className={styles.nearbyShopsContainer}>
      <div className={styles.nearbyShopsTitleContainer}>
        <p className={styles.nearbyShopsTitle}>{__("store_locator.popup.nearby_shops")}</p>
      </div>

      {stores.length > 0 && (
        <Carousel isViewportHeight={true} desktopItemCount={3} tabletItemCount={1}>
          {stores.map((store) => (
            <StoreDetailsNearbyShopsCard {...rest} key={store.id} store={store} />
          ))}
        </Carousel>
      )}
    </div>
  );
};
